import Bugsnag from "@bugsnag/js";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import InputSmily from "@Components/atoms/InputSmily";
import CheckoutStep from "@Components/molecules/cart/CheckoutStep";
import useCartContext from "@Hooks/useCartContext";
import { ReactComponent as IconProfil } from "@Icons/colored/profil.svg";
import { ApiErrorResponse } from "@Types/api/error";
import { FormError } from "@Types/error";
import { UserDetails } from "@Types/user";
import { checkEmailFormat, emailPattern } from "@Utils/checkFormats";
import { getInputErrorMessages } from "@Utils/error";
import { convertUserDetailsFromContentToApi, convertUserFromApiToContent } from "@Utils/user";
import "./style.scss";

interface Props {
  disabled: boolean;
  errors: FormError[];
  onValidate: (userInfos: UserDetails, callback: () => void) => void;
  removeErrors: () => void;
  userInfos: UserDetails;
}

export const CheckoutContactStep: FunctionComponent<Props> = ({
  disabled,
  errors,
  onValidate,
  removeErrors,
  userInfos,
}) => {
  const intl = useIntl();
  const [editable, setEditable] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [confirmEmailInput, setConfirmEmailInput] = useState<string | undefined>(
    userInfos.confirmEmail
  );
  const [emailInput, setEmailInput] = useState<string | undefined>(userInfos.email);
  const [firstNameInput, setFirstNameInput] = useState<string | undefined>(userInfos.firstName);
  const [lastNameInput, setLastNameInput] = useState<string | undefined>(userInfos.lastName);

  const handleChangeInputValue = (input: string, value: string) => {
    removeErrors();
    switch (input) {
      case "confirmEmail":
        setConfirmEmailInput(value);
        break;
      case "email":
        setEmailInput(value);
        break;
      case "firstName":
        setFirstNameInput(value);
        break;
      case "lastName":
        setLastNameInput(value);
        break;
    }
  };

  const handleValidate = () =>
    onValidate(
      {
        confirmEmail: confirmEmailInput,
        email: emailInput,
        firstName: firstNameInput,
        lastName: lastNameInput,
      },
      () => {
        setEditable(true);
        setValidated(true);
      }
    );

  useEffect(() => {
    if (!!userInfos.confirmEmail && !!userInfos.email) {
      setEditable(true);
      setValidated(true);
    }
  }, []);

  return (
    <div className="CheckoutContactStep">
      <CheckoutStep
        buttonText={intl.formatMessage({
          id: "cheerz_next",
        })}
        disabled={disabled || errors.length > 0 || !emailInput || !confirmEmailInput}
        displayButton={true}
        editable={editable}
        icon={<IconProfil />}
        onEdit={() => setEditable(!editable)}
        onValidate={handleValidate}
        title={intl.formatMessage({ id: "cheerz_CheckoutContactStep_title" })}
        validated={validated}
      >
        {editable ? (
          <div className="CheckoutContactStep-editable">
            <div className="CheckoutContactStep-editable-description">
              <div>
                {userInfos.firstName} {userInfos.lastName}
              </div>
              {userInfos.email}
            </div>
          </div>
        ) : (
          <div className="CheckoutContactStep-content">
            <div className="CheckoutContactStep-description">
              {intl.formatMessage({ id: "cheerz_CheckoutContactStep_description" })}
            </div>
            <div className="CheckoutContactStep-inputs">
              <InputSmily
                disabled={disabled}
                onChange={(value) => handleChangeInputValue("firstName", value)}
                onDismiss={() => handleChangeInputValue("firstName", "")}
                placeholder={intl.formatMessage({ id: "cheerz_first_name" })}
                type="text"
                value={firstNameInput}
              />
              <InputSmily
                disabled={disabled}
                onChange={(value) => handleChangeInputValue("lastName", value)}
                onDismiss={() => handleChangeInputValue("lastName", "")}
                placeholder={intl.formatMessage({ id: "cheerz_last_name" })}
                type="text"
                value={lastNameInput}
              />
              <InputSmily
                disabled={disabled}
                errorMessage={getInputErrorMessages(errors, "email")}
                onChange={(value) => {
                  handleChangeInputValue("email", value);
                  setConfirmEmailInput("");
                }}
                onDismiss={() => handleChangeInputValue("email", "")}
                pattern={emailPattern}
                placeholder={intl.formatMessage({ id: "cheerz_email" })}
                required={true}
                type="email"
                value={emailInput}
              />
              <InputSmily
                disabled={disabled}
                errorMessage={getInputErrorMessages(errors, "confirmEmail")}
                onChange={(value) => handleChangeInputValue("confirmEmail", value)}
                onDismiss={() => handleChangeInputValue("confirmEmail", "")}
                pattern={emailPattern}
                placeholder={intl.formatMessage({ id: "cheerz_confirm_email" })}
                required={true}
                type="email"
                value={confirmEmailInput}
              />
            </div>
          </div>
        )}
      </CheckoutStep>
    </div>
  );
};

const CheckoutContactStepContainer: FunctionComponent = () => {
  const { addresses, configuration, dispatch, ui, user } = useCartContext();
  const intl = useIntl();
  const [formErrors, setFormErrors] = useState<FormError[]>([]);
  const email = user.email || addresses.delivery.email; // TODO: remove this when addresses will be put after Contact details

  const handleValidate = (userInfos: UserDetails, callback: () => void) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });
    if (userInfos.confirmEmail !== userInfos.email) {
      dispatch({
        disabled: false,
        type: "setDisabledCart",
      });
      setFormErrors([
        {
          input: "confirmEmail",
          message: intl.formatMessage({
            id: "cheerz_Notification_CFE-003",
          }),
        },
      ]);
    } else {
      checkEmailFormat(userInfos.email)
        .then((validatedEmail) => {
          if (configuration) {
            const updatedUserDetails = convertUserDetailsFromContentToApi({
              email: validatedEmail,
              firstName: userInfos.firstName,
              lastName: userInfos.lastName,
            });

            configuration.apiProviders.cheerz
              .updateUserProfile(updatedUserDetails)
              .then((userResponse) => {
                dispatch({
                  type: "setUserProfile",
                  user: convertUserFromApiToContent(userResponse),
                });
                dispatch({
                  disabled: false,
                  type: "setDisabledCart",
                });
                callback();
              })
              .catch((error: ApiErrorResponse) => {
                dispatch({
                  disabled: false,
                  type: "setDisabledCart",
                });
                setFormErrors([
                  {
                    input: "email",
                    message: error.response?.data?.error_description[0]
                      ? `${error.response?.data?.error_description[0]}`
                      : intl.formatMessage({
                          id: "cheerz_Notification_CFE-001",
                        }),
                  },
                ]);
                Bugsnag.notify(
                  Error(
                    error.response?.data?.error_description[0]
                      ? `${error.response?.data?.error_description[0]}`
                      : "Error on saveCart call"
                  )
                );
              });
          }
        })
        .catch((errorCode: string) => {
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
          setFormErrors([
            {
              input: "email",
              message: intl.formatMessage({
                id: `cheerz_Notification_${errorCode}`,
              }),
            },
          ]);
        });
    }
  };

  return (
    <CheckoutContactStep
      disabled={ui.disabled}
      errors={formErrors}
      onValidate={handleValidate}
      removeErrors={() => setFormErrors([])}
      userInfos={{ confirmEmail: email, email, firstName: user.firstName, lastName: user.lastName }}
    />
  );
};

export default CheckoutContactStepContainer;
